import "./map.less"
import PresentationQuestion from "../components/media/presentation-question"
import { useMessages } from "../language/language"
import {
  ClientScreen,
  MapAnswer,
  PlacePinPayload,
  PlayMapState,
} from "../play-model"
import { classNames, hasText } from "utils/helpers"
import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from "react"
import { usePlayState } from "play/hooks/use-play-state"
import { ScreenType } from "models/tables"

export default function Map() {
  const { screen, showCorrectAnswers, isTeacherUrl, map, modifyState } =
    usePlayState()

  function placeMapPin(payload: PlacePinPayload) {
    modifyState((state) => {
      if (!state.screen) {
        console.warn("No current screen!")
        return
      }
      if (state.screen.screenType !== ScreenType.Map) {
        console.warn("Not a map screen!")
        return
      }
      if (state.finished) return

      const normalizedPin = payload.normalizedPin

      state.map = {
        normalizedPin: normalizedPin,
      }
      state.nextScreenButtonIsActive = true

      // debugging
      /*
      const ok = isPinInsideAnyAnswer(pin, screen.mapAnswers);
    
      console.log("pin:", pin, "idx:", ok, "answers:", screen.mapAnswers.map(a => ({
        x: a.x,
        y: a.y,
        w: a.width,
        h: a.height
      })));
      */
    })
  }

  if (screen) {
    return (
      <MapScreenView
        questionImage={screen.questionImage}
        questionText={screen.questionText}
        mapAnswers={screen.mapAnswers}
        isTeacher={isTeacherUrl}
        normalizedPin={map.normalizedPin}
        showCorrect={showCorrectAnswers}
        placeMapPin={placeMapPin}
      />
    )
  } else {
    return (
      <MapScreenView
        questionImage={null}
        questionText={null}
        mapAnswers={[]}
        isTeacher={isTeacherUrl}
        normalizedPin={map.normalizedPin}
        showCorrect={showCorrectAnswers}
        placeMapPin={placeMapPin}
      />
    )
  }
}

export function MapScreenView(props: {
  showCorrect: boolean
  normalizedPin: PlayMapState["normalizedPin"]
  isTeacher: boolean
  questionText: string | null
  questionImage: string | null
  mapAnswers: ClientScreen["mapAnswers"]

  placeMapPin(pin: PlacePinPayload): void
}) {
  const {
    showCorrect,
    normalizedPin,
    isTeacher,
    questionText,
    questionImage,
    mapAnswers,
    placeMapPin,
  } = props
  const msg = useMessages()
  const alt = msg.map_alt
  const screenElement = useRef<HTMLDivElement>(null)
  const imageElement = useRef<HTMLImageElement>(null)
  const imageContainerElement = useRef<HTMLDivElement>(null)
  const [state, setState] = useState({
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    naturalWidth: 0,
    naturalHeight: 0,
    maxWidth: 0,
    maxHeight: 0,
  })

  function calculateState(reason: string) {
    setState((prev) => {
      const next = { ...prev }

      if (
        screenElement.current &&
        imageElement.current &&
        imageContainerElement.current
      ) {
        const maxRt = imageContainerElement.current.getBoundingClientRect()

        const nw = imageElement.current.naturalWidth
        const nh = imageElement.current.naturalHeight
        const maxW = maxRt.width
        const maxH = maxRt.height

        next.naturalWidth = nw
        next.naturalHeight = nh
        next.maxWidth = maxW
        next.maxHeight = maxH

        if (maxW >= maxH) {
          // landscape container
          // limiting factor is height

          next.h = maxH
          next.w = (nw / nh) * next.h
          while (next.w > maxW) {
            next.h *= 0.99
            next.w = (nw / nh) * next.h
          }
        } else {
          // portrait container
          // limiting factor is width

          next.w = maxW
          next.h = (nh / nw) * next.w
          while (next.h > maxH) {
            next.w *= 0.99
            next.h = (nh / nw) * next.w
          }
        }

        next.x = (next.maxWidth - next.w) / 2
        next.y = (next.maxHeight - next.h) / 2

        console.log(
          '"%s" new size: %d,%d %dx%d, max: %dx%d || %s',
          reason,
          next.x,
          next.y,
          next.w,
          next.h,
          next.maxWidth,
          next.maxHeight,
          questionImage
        )
      } else {
        console.log(
          '"%s" can\'t recalculate size map=%o img=%o',
          reason,
          screenElement.current,
          imageElement.current
        )
      }

      return next
    })
  }

  useEffect(() => {
    calculateState("effect")

    const calculateOnResize = calculateState.bind(null, "window resize")
    const calculateOnLoad = calculateState.bind(null, "load")

    if (imageElement.current) {
      imageElement.current.addEventListener("load", calculateOnLoad)
    }

    window.addEventListener("resize", calculateOnResize)
    return () => {
      if (imageElement.current) {
        imageElement.current.removeEventListener("load", calculateOnLoad)
      }

      window.removeEventListener("resize", calculateOnResize)
    }
  }, [questionImage])

  function handleClicked(e: ReactMouseEvent<HTMLImageElement>) {
    if (isTeacher || showCorrect) return

    const rt = e.currentTarget.getBoundingClientRect()

    const x = e.clientX - rt.x
    const y = e.clientY - rt.y

    const w = rt.width
    const h = rt.height

    console.log(e.clientX, e.clientY, rt.x, rt.y)

    const normalizedX = x / w
    const normalizedY = y / h

    console.log("pin %.2fx%.2f", normalizedX, normalizedY)

    placeMapPin({
      normalizedPin: { x: normalizedX, y: normalizedY },
    })
  }

  let pinStyle: CSSProperties = {}
  if (normalizedPin) {
    const pixelWidth = 26
    const pixelHeight = 36
    const pixelX = state.x + normalizedPin.x * state.w
    const pixelY = state.y + normalizedPin.y * state.h

    pinStyle.left = pixelX - pixelWidth / 2
    pinStyle.top = pixelY - pixelHeight
    pinStyle.width = pixelWidth
    pinStyle.height = pixelHeight
  }

  const wOrH = state.naturalWidth >= state.naturalHeight ? "w" : "h"

  return (
    <div
      className={classNames("map-screen play-screen", wOrH)}
      ref={screenElement}
    >
      <div className="question-container">
        <PresentationQuestion question={questionText} />
      </div>
      <div className="map-container">
        <div className="instructions">
          <span className="text">{msg.map_instruction}</span>
          <i />
        </div>
        {hasText(questionImage) && (
          <div className="map" ref={imageContainerElement}>
            <img
              ref={imageElement}
              className={classNames(wOrH, {
                enabled: !isTeacher && !showCorrect,
              })}
              style={{
                top: state.y,
                left: state.x,
                width: state.w,
                height: state.h,
              }}
              src={questionImage || ""}
              alt={alt}
              data-w={state.w || 0}
              data-h={state.h || 0}
              data-x={state.x || 0}
              data-y={state.y || 0}
              data-nx={state.naturalWidth}
              data-ny={state.naturalHeight}
              draggable={false}
              onClick={handleClicked}
            />

            {normalizedPin && (
              <div
                className="pin"
                style={pinStyle}
                draggable={false}
                data-x={normalizedPin.x}
                data-y={normalizedPin.y}
              />
            )}
            {showCorrect &&
              mapAnswers.map((a) => (
                <MapEllipseAnswer key={a.id} answer={a} imgRect={state} />
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

function MapEllipseAnswer(props: {
  answer: MapAnswer
  imgRect: { x: number; y: number; w: number; h: number }
}) {
  const answer = props.answer
  const imageWidth = props.imgRect.w
  const imageHeight = props.imgRect.h
  const offsetX = props.imgRect.x
  const offsetY = props.imgRect.y

  const pixelWidth = answer.width * imageWidth
  const pixelHeight = answer.height * imageHeight
  const pixelRadiusX = answer.x * imageWidth
  const pixelRadiusY = answer.y * imageHeight
  const pixelX = offsetX + pixelRadiusX - pixelWidth / 2
  const pixelY = offsetY + pixelRadiusY - pixelHeight / 2

  const borderSize = 2

  const styleResize: CSSProperties = {
    position: "absolute",
    top: pixelY,
    left: pixelX,
    width: pixelWidth,
    height: pixelHeight,
    borderRadius: pixelWidth / 2,
    borderWidth: borderSize,
  }
  const styleMove: CSSProperties = {
    position: "absolute",
    width: pixelWidth - borderSize * 2,
    height: pixelHeight - borderSize * 2,
    borderRadius: (pixelWidth - borderSize) / 2,
    borderWidth: borderSize,
  }

  return (
    <div
      style={styleResize}
      className="map-answer-outer"
      data-x={answer.x}
      data-y={answer.y}
      data-w={answer.width}
      data-h={answer.height}
    >
      <div style={styleMove} className="map-answer-inner" />
    </div>
  )
}
